import ScheduleType from "./ScheduleType";
import {
  MAX_CLIENTS_DISPLAYED_PER_SLOT,
  ResolvedScheduleCell,
  ScheduleGridSlot,
  SlotVisualKind,
} from "./scheduleGridTypes";

export function parseDisplayDateToIso(dateDdMmYyyy: string): string {
  const [day, month, year] = dateDdMmYyyy.split("/");
  return `${year}-${month}-${day}`;
}

export function buildScheduleCoord(
  dateDdMmYyyy: string,
  hour: string,
  consultantId: number
): string {
  const isoDate = parseDisplayDateToIso(dateDdMmYyyy);
  const normalizedHour = hour.includes(":") ? hour : `${hour}:00`;
  const timePart = normalizedHour.length === 5 ? `${normalizedHour}:00` : normalizedHour;
  return `${isoDate}|${timePart}|${consultantId}`;
}

function normalizeScheduleDateParts(date: string) {
  const [agDateStr, agTimeStr] = date.replace("T", " ").split(" ");
  return {
    agDateStr,
    agHour: agTimeStr ? agTimeStr.substring(0, 2) : "",
  };
}

export function filterSchedulesForCell(
  schedules: ScheduleGridSlot[],
  cellDateIso: string,
  cellHour: string,
  consultantId: number,
  options?: { includeCanceled?: boolean }
): ScheduleGridSlot[] {
  const hourKey = cellHour.split(":")[0];

  return schedules.filter((scheduleItem) => {
    if (
      !options?.includeCanceled &&
      scheduleItem.status === ScheduleType.CANCELED
    ) {
      return false;
    }

    const { agDateStr, agHour } = normalizeScheduleDateParts(scheduleItem.date);

    return (
      agDateStr === cellDateIso &&
      agHour === hourKey &&
      scheduleItem.consultant_id === consultantId
    );
  });
}

export function summarizeClientNames(
  appointments: ScheduleGridSlot[]
): ResolvedScheduleCell["clientSummary"] {
  const names = appointments
    .map((item) => item.client?.first_name?.trim())
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) {
    return { badge: null, label: "", tooltip: "" };
  }

  const uniqueNames = [...new Set(names)];
  const total = uniqueNames.length;
  const badge =
    total > 1
      ? `${total}/${MAX_CLIENTS_DISPLAYED_PER_SLOT}`
      : null;

  let label = uniqueNames[0];
  if (total === 2) {
    label = `${uniqueNames[0]}, ${uniqueNames[1]}`;
  } else if (total > 2) {
    label = `${uniqueNames[0]}, +${total - 1}`;
  }

  return {
    badge,
    label,
    tooltip: uniqueNames.join(", "),
  };
}

function resolveVisualKind(
  appointments: ScheduleGridSlot[],
  fallback: ScheduleGridSlot | undefined
): SlotVisualKind {
  const scheduled = appointments.filter(
    (item) => item.status === ScheduleType.SCHEDULED
  );

  if (scheduled.length > 0) {
    const hasVideo = scheduled.some((item) => item.video_call === 1);
    const hasInPerson = scheduled.some((item) => item.video_call === 0);
    if (hasVideo && hasInPerson) {
      return "mixed";
    }
    return hasVideo ? "video" : "scheduled";
  }

  const slot = fallback ?? appointments.sort((a, b) => b.status - a.status)[0];
  if (!slot) {
    return "empty";
  }

  if (appointments.length === 0 && slot.status === ScheduleType.SCHEDULED) {
    // Defensive: scheduled rows should be in appointments list.
    return "scheduled";
  }

  if (slot.status === ScheduleType.UNLOCKED) {
    return "free";
  }

  if (slot.status === ScheduleType.LOCKED) {
    return "blocked";
  }

  return "empty";
}

function resolveOutcomeVisualKind(
  appointments: ScheduleGridSlot[]
): SlotVisualKind | null {
  if (appointments.length === 0) {
    return null;
  }

  const outcomes = appointments
    .map((item) => item.outcome)
    .filter((value): value is NonNullable<ScheduleGridSlot["outcome"]> =>
      Boolean(value)
    );

  if (outcomes.length === 0) {
    return null;
  }

  // One client showing up is enough for the hour to count as completed (who came and
  // who did not is listed in the visit details). No-show only when nobody came.
  if (outcomes.includes("completed")) {
    return "completed";
  }
  if (outcomes.includes("no_show")) {
    return "no_show";
  }
  if (outcomes.includes("video") && outcomes.includes("scheduled")) {
    return "mixed";
  }
  if (outcomes.includes("video")) {
    return "video";
  }
  if (outcomes.includes("scheduled")) {
    return "scheduled";
  }

  return null;
}

export function resolveScheduleCell(params: {
  dateDdMmYyyy: string;
  hour: string;
  consultantId: number;
  schedules: ScheduleGridSlot[];
}): ResolvedScheduleCell {
  const { dateDdMmYyyy, hour, consultantId, schedules } = params;
  const cellDateIso = parseDisplayDateToIso(dateDdMmYyyy);
  const coord = buildScheduleCoord(dateDdMmYyyy, hour, consultantId);

  const cellSchedules = filterSchedulesForCell(
    schedules,
    cellDateIso,
    hour,
    consultantId
  );

  // An hour is only bookable after it is unlocked, which creates the row. Without a row
  // the slot is blocked, as on the client site and in the legacy agenda.
  if (cellSchedules.length === 0) {
    return {
      coord,
      visualKind: "blocked",
      displayStatus: ScheduleType.LOCKED,
      primarySlotId: 0,
      appointments: [],
      blockedNote: null,
      hasNote: false,
      clientSummary: summarizeClientNames([]),
    };
  }

  const appointments = cellSchedules.filter(
    (item) => item.status === ScheduleType.SCHEDULED
  );

  const fallback = cellSchedules.sort((a, b) => b.status - a.status)[0];
  const outcomeKind = resolveOutcomeVisualKind(appointments);
  const visualKind = outcomeKind ?? resolveVisualKind(appointments, fallback);
  const displayStatus =
    appointments.length > 0
      ? ScheduleType.SCHEDULED
      : fallback?.status ?? ScheduleType.LOCKED;

  const blockedSlot = cellSchedules.find(
    (item) => item.status === ScheduleType.LOCKED && item.note?.trim()
  );
  const blockedNote = blockedSlot?.note?.trim() ?? null;
  const hasNote = cellSchedules.some((item) => Boolean(item.note?.trim()));

  const primarySlotId =
    appointments[0]?.id ?? fallback?.id ?? 0;

  return {
    coord,
    visualKind,
    displayStatus,
    primarySlotId,
    appointments,
    blockedNote,
    hasNote,
    clientSummary: summarizeClientNames(appointments),
  };
}

export function countConsultantDayStats(
  schedules: ScheduleGridSlot[],
  dateDdMmYyyy: string,
  consultantId: number,
  businessHours: string[]
): { visits: number; freeSlots: number } {
  const cellDateIso = parseDisplayDateToIso(dateDdMmYyyy);
  let visits = 0;
  let freeSlots = 0;

  for (const hour of businessHours) {
    const cellSchedules = filterSchedulesForCell(
      schedules,
      cellDateIso,
      hour,
      consultantId
    );
    const appointments = cellSchedules.filter(
      (item) => item.status === ScheduleType.SCHEDULED
    );
    visits += appointments.length;

    const hasFree = cellSchedules.some(
      (item) => item.status === ScheduleType.UNLOCKED
    );
    if (hasFree) {
      freeSlots += 1;
    }
  }

  return { visits, freeSlots };
}

export type SlotOccupancyKind = "scheduled" | "free" | "blocked";

/**
 * Occupancy only considers hours open to clients (scheduled + free); blocked hours,
 * including the ones without a row, stay out of the base.
 */
export function classifySlotOccupancy(
  cellSchedules: Pick<ScheduleGridSlot, "status">[]
): SlotOccupancyKind {
  if (cellSchedules.some((item) => item.status === ScheduleType.SCHEDULED)) {
    return "scheduled";
  }
  if (cellSchedules.some((item) => item.status === ScheduleType.UNLOCKED) &&
    !cellSchedules.some((item) => item.status === ScheduleType.LOCKED)) {
    return "free";
  }
  return "blocked";
}

export function countCityOccupancy(
  schedules: ScheduleGridSlot[],
  dateDdMmYyyy: string,
  consultantIds: number[],
  businessHours: string[]
): { occupied: number; total: number } {
  const cellDateIso = parseDisplayDateToIso(dateDdMmYyyy);
  let occupied = 0;
  let total = 0;

  for (const consultantId of consultantIds) {
    for (const hour of businessHours) {
      const cellSchedules = filterSchedulesForCell(
        schedules,
        cellDateIso,
        hour,
        consultantId
      );
      const kind = classifySlotOccupancy(cellSchedules);
      if (kind !== "blocked") {
        total += 1;
      }
      if (kind === "scheduled") {
        occupied += 1;
      }
    }
  }

  return { occupied, total };
}

export function getConsultantInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function getFortalezaCurrentHourKey(): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Fortaleza",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  return `${hour.padStart(2, "0")}:00`;
}

export function computeDayGridSummary(
  schedules: ScheduleGridSlot[],
  dateDdMmYyyy: string,
  consultantIds: number[],
  businessHours: string[]
): { visits: number; freeSlots: number; occupancyPercent: number } {
  let visits = 0;
  let freeSlots = 0;
  let occupiedSlots = 0;
  let totalSlots = 0;

  for (const consultantId of consultantIds) {
    const stats = countConsultantDayStats(
      schedules,
      dateDdMmYyyy,
      consultantId,
      businessHours
    );
    visits += stats.visits;
    freeSlots += stats.freeSlots;
  }

  for (const consultantId of consultantIds) {
    const { occupied, total } = countCityOccupancy(
      schedules,
      dateDdMmYyyy,
      [consultantId],
      businessHours
    );
    occupiedSlots += occupied;
    totalSlots += total;
  }

  const occupancyPercent =
    totalSlots > 0 ? Math.round((occupiedSlots / totalSlots) * 100) : 0;

  return { visits, freeSlots, occupancyPercent };
}

export function isSameDisplayDateAsFortalezaToday(
  dateDdMmYyyy: string
): boolean {
  const fortalezaDate = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Fortaleza",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date());

  const [day, month, year] = dateDdMmYyyy.split("/");
  const localFormatted = `${day}/${month}/${year}`;
  return localFormatted === fortalezaDate;
}
